import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile, deleteAccount, fetchCurrentUser } from '../api';

const QUICK_LINKS = [
  { id: 'headline',       label: 'Resume headline' },
  { id: 'keySkills',      label: 'Key skills' },
  { id: 'employment',     label: 'Employment' },
  { id: 'education',      label: 'Education' },
  { id: 'profileSummary', label: 'Profile summary' },
  { id: 'personalDetails',label: 'Personal details' },
];

const EMPTY_EMPLOYMENT = { role: '', company: '', from: '', to: '', current: false };
const EMPTY_EDUCATION  = { degree: '', institution: '', year: '', grade: '' };

export default function Profile({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('headline');
  const [deleteError, setDeleteError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const handleDeleteAccount = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    try {
      await deleteAccount();
      if (onLogout) onLogout();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account. Please try again.');
    }
  };
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [saveError, setSaveError] = useState('');

  const [profileData, setProfileData] = useState(buildFromUser(user));

  // Fetch fresh full profile from server on mount (session may lack extended fields)
  useEffect(() => {
    fetchCurrentUser().then(fresh => {
      if (fresh) {
        setProfileData(buildFromUser(fresh));
        if (onUserUpdate) onUserUpdate(fresh);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function buildFromUser(u) {
    return {
      displayName:    u?.displayName    || '',
      username:       u?.username       || '',
      headline:       u?.headline       || '',
      currentRole:    u?.currentRole    || '',
      currentCompany: u?.currentCompany || '',
      location:       u?.location       || '',
      email:          u?.email          || '',
      phone:          u?.phone          || '',
      bio:            u?.bio            || '',
      linkedin:       u?.linkedin       || '',
      github:         u?.github         || '',
      skills:         u?.skills         || [],
      employment:     u?.employment     || [],
      education:      u?.education      || [],
      helpfulVotes:   u?.helpfulVotes   || 0,
      totalKahanis:   u?.totalKahanis   || 0,
      avatarUrl:      u?.avatarUrl      || null,
    };
  }

  const userInitial = profileData.displayName?.charAt(0).toUpperCase() || 'U';
  const profilePct  = Math.min(100,
    20 +
    (profileData.headline        ? 10 : 0) +
    (profileData.bio             ? 15 : 0) +
    (profileData.skills.length   ? 15 : 0) +
    (profileData.employment.length? 15 : 0) +
    (profileData.education.length ? 10 : 0) +
    (profileData.linkedin        ? 5  : 0) +
    (profileData.phone           ? 5  : 0) +
    (profileData.totalKahanis    ? 5  : 0)
  );
  const circumference = 2 * Math.PI * 54; // r=54
  const offset = circumference - (profilePct / 100) * circumference;

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openEdit = (section) => {
    setSaveError('');
    setEditSection(section);
    setIsEditModalOpen(true);
  };

  const handleSave = async (newData) => {
    setSaveError('');
    try {
      await updateProfile(newData);
      setProfileData(prev => ({ ...prev, ...newData }));
      if (onUserUpdate) onUserUpdate(newData);
      setIsEditModalOpen(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to update profile. Please try again.');
    }
  };

  return (
    <div className="profile-page-wrapper" style={{ background: 'var(--bg2)', minHeight: '100vh', padding: '24px 0' }}>
      <div className="profile-page-grid" style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '240px 1fr', gap: 24, padding: '0 20px' }}>

        {/* Quick Links Sidebar */}
        <aside className="profile-aside" style={{ position: 'sticky', top: 94, height: 'fit-content' }}>
          <div style={{ background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: 'var(--text2)', borderBottom: '1px solid var(--border)' }}>
              Quick links
            </div>
            {QUICK_LINKS.map(link => (
              <div
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                style={{
                  padding: '12px 20px', fontSize: 12, cursor: 'pointer',
                  color: activeSection === link.id ? 'var(--blue)' : 'var(--text3)',
                  fontWeight: activeSection === link.id ? 700 : 500,
                  borderLeft: `3px solid ${activeSection === link.id ? 'var(--blue)' : 'transparent'}`,
                  background: activeSection === link.id ? 'var(--surface2)' : 'var(--surface)',
                  transition: '0.2s all',
                }}
              >
                {link.label}
              </div>
            ))}
          </div>
        </aside>

        {/* Main */}
        <main style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Profile Header Card ── */}
          <SectionCard noPadding>
            <div className="profile-header-row" style={{ padding: '24px', display: 'flex', gap: 24, alignItems: 'center' }}>
              {/* Avatar with progress ring */}
              <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="60" cy="60" r="54" fill="none" stroke="var(--border)" strokeWidth="5" />
                  <circle cx="60" cy="60" r="54" fill="none" stroke="var(--blue)" strokeWidth="5"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                  />
                </svg>
                {profileData.avatarUrl ? (
                  <img src={profileData.avatarUrl} alt="avatar" style={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, fontWeight: 700, color: '#d97706' }}>
                    {userInitial}
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: -5, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 700, color: 'var(--blue)', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                  {profilePct}%
                </div>
              </div>

              {/* Name / role / location */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: 'var(--text)' }}>
                  {profileData.displayName || <span style={{ color: 'var(--text3)' }}>Your Name</span>}
                </h1>
                {profileData.headline && (
                  <div style={{ fontSize: 14, color: 'var(--blue)', fontWeight: 600, marginBottom: 6 }}>{profileData.headline}</div>
                )}
                <div style={{ fontSize: 13, color: 'var(--text3)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {(profileData.currentRole || profileData.currentCompany) && (
                    <span>💼 {[profileData.currentRole, profileData.currentCompany].filter(Boolean).join(' @ ')}</span>
                  )}
                  {profileData.location && <span>📍 {profileData.location}</span>}
                  {profileData.email    && <span>📧 {profileData.email}</span>}
                </div>
                <button
                  onClick={() => openEdit('header')}
                  style={{ marginTop: 12, padding: '6px 16px', borderRadius: 20, border: '1px solid var(--blue)', background: 'transparent', color: 'var(--blue)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  Edit profile
                </button>
              </div>

              {/* Stats */}
              <div className="profile-stats-col" style={{ width: 180, borderLeft: '1px solid var(--border)', paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <StatBox label="Kahanis Shared" value={profileData.totalKahanis} />
                <StatBox label="Helpful Votes"  value={profileData.helpfulVotes} />
              </div>
            </div>
          </SectionCard>

          {/* ── Resume Headline ── */}
          <Section id="headline" title="Resume headline" onEdit={() => openEdit('headline')}>
            {profileData.headline
              ? <p style={{ fontSize: 14, color: 'var(--text2)', margin: 0, lineHeight: 1.5 }}>{profileData.headline}</p>
              : <AddPrompt text="Add a headline like: Senior SDE · 5 yrs · React, Node.js, AWS" />
            }
          </Section>

          {/* ── Key Skills ── */}
          <Section id="keySkills" title="Key skills" onEdit={() => openEdit('skills')}>
            {profileData.skills.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {profileData.skills.map(skill => (
                  <span key={skill} style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border2)', background: 'var(--surface2)', fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>
                    {skill}
                  </span>
                ))}
              </div>
            ) : <AddPrompt text="Add your technical and soft skills" />}
          </Section>

          {/* ── Employment ── */}
          <Section id="employment" title="Employment" onEdit={() => openEdit('employment')}>
            {profileData.employment.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {profileData.employment.map((job, i) => (
                  <EmploymentRow key={i} job={job} isLast={i === profileData.employment.length - 1} />
                ))}
              </div>
            ) : <AddPrompt text="Add your work experience — company, role, and tenure" />}
          </Section>

          {/* ── Education ── */}
          <Section id="education" title="Education" onEdit={() => openEdit('education')}>
            {profileData.education.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {profileData.education.map((edu, i) => (
                  <EducationRow key={i} edu={edu} isLast={i === profileData.education.length - 1} />
                ))}
              </div>
            ) : <AddPrompt text="Add your degree, institution and year of passing" />}
          </Section>

          {/* ── Profile Summary ── */}
          <Section id="profileSummary" title="Profile summary" onEdit={() => openEdit('bio')}>
            {profileData.bio
              ? <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, margin: 0 }}>{profileData.bio}</p>
              : <AddPrompt text="Write a brief summary about your professional background and goals" />
            }
          </Section>

          {/* ── Personal Details ── */}
          <Section id="personalDetails" title="Personal details" onEdit={() => openEdit('personal')}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
              <DetailRow icon="👤" label="Full Name"  value={profileData.displayName} />
              <DetailRow icon="📧" label="Email"      value={profileData.email} />
              <DetailRow icon="📱" label="Phone"      value={profileData.phone} />
              <DetailRow icon="📍" label="Location"   value={profileData.location} />
              <DetailRow icon="🔗" label="LinkedIn"   value={profileData.linkedin} isLink />
              <DetailRow icon="🐙" label="GitHub"     value={profileData.github}   isLink />
            </div>
          </Section>

          {/* ── Delete Account ── */}
          <SectionCard>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#ef4444', margin: '0 0 8px' }}>Delete Account</h3>
            <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 16px', lineHeight: 1.6 }}>
              Permanently delete your account and all your kahanis. This action cannot be undone.
            </p>
            {deleteError && (
              <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
                {deleteError}
              </div>
            )}
            {deleteConfirm && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626', fontWeight: 600, marginBottom: 12 }}>
                Are you sure? Click again to permanently delete your account.
              </div>
            )}
            <button
              onClick={handleDeleteAccount}
              style={{
                padding: '10px 24px', borderRadius: 8, border: '1.5px solid #ef4444',
                background: deleteConfirm ? '#ef4444' : 'transparent',
                color: deleteConfirm ? '#fff' : '#ef4444',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { if (!deleteConfirm) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ef4444'; } }}
            >
              {deleteConfirm ? 'Yes, Delete My Account' : 'Delete Account'}
            </button>
          </SectionCard>

        </main>
      </div>

      {isEditModalOpen && (
        <QuickEditModal
          section={editSection}
          data={profileData}
          onSave={handleSave}
          onClose={() => setIsEditModalOpen(false)}
          saveError={saveError}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────

function StatBox({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function AddPrompt({ text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', color: 'var(--text3)', fontSize: 13 }}>
      <span style={{ fontSize: 20 }}>＋</span>
      <span>{text}</span>
    </div>
  );
}

function DetailRow({ icon, label, value, isLink }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      {value ? (
        isLink ? (
          <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer"
            style={{ fontSize: 13, color: 'var(--blue)', fontWeight: 500, wordBreak: 'break-all' }}>
            {icon} {value}
          </a>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>{icon} {value}</div>
        )
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>Not provided</div>
      )}
    </div>
  );
}

function EmploymentRow({ job, isLast }) {
  return (
    <div style={{ paddingBottom: isLast ? 0 : 16, borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
          🏢
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{job.role || '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 1 }}>{job.company}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
            {job.from} — {job.current ? 'Present' : (job.to || '—')}
          </div>
        </div>
        {job.current && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>Current</span>
        )}
      </div>
    </div>
  );
}

function EducationRow({ edu, isLast }) {
  return (
    <div style={{ paddingBottom: isLast ? 0 : 16, borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
          🎓
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{edu.degree || '—'}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 1 }}>{edu.institution}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
            {edu.year}{edu.grade ? ` · ${edu.grade}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, children, onEdit }) {
  return (
    <SectionCard id={id}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{title}</h3>
        <span onClick={onEdit} style={{ color: 'var(--blue)', fontSize: 13, fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
          Add / Edit
        </span>
      </div>
      {children}
    </SectionCard>
  );
}

function SectionCard({ id, children, noPadding = false }) {
  return (
    <section id={id} style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: noPadding ? 0 : '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      {children}
    </section>
  );
}

// ── Edit Modal ──────────────────────────────────────────────────

function QuickEditModal({ section, data, onSave, onClose, saveError }) {
  const [formData, setFormData] = useState({ ...data });
  // local copies of arrays so we can add/remove rows before saving
  const [empList, setEmpList] = useState(data.employment?.length ? data.employment.map(e => ({ ...e })) : [{ ...EMPTY_EMPLOYMENT }]);
  const [eduList, setEduList] = useState(data.education?.length  ? data.education.map(e => ({ ...e }))  : [{ ...EMPTY_EDUCATION  }]);

  const TITLES = {
    header:     'Edit Profile',
    headline:   'Resume Headline',
    skills:     'Key Skills',
    employment: 'Employment',
    education:  'Education',
    bio:        'Profile Summary',
    personal:   'Personal Details',
  };

  const handleSaveClick = () => {
    if (section === 'employment') return onSave({ employment: empList.filter(j => j.company || j.role) });
    if (section === 'education')  return onSave({ education: eduList.filter(e => e.degree || e.institution) });
    if (section === 'skills')     return onSave({ skills: formData.skills });
    if (section === 'headline')   return onSave({ headline: formData.headline });
    if (section === 'bio')        return onSave({ bio: formData.bio });
    if (section === 'header') {
      const payload = { currentRole: formData.currentRole, currentCompany: formData.currentCompany, location: formData.location };
      if (formData.displayName) payload.displayName = formData.displayName;
      if (formData.username)    payload.username    = formData.username;
      return onSave(payload);
    }
    if (section === 'personal') {
      const payload = { phone: formData.phone, location: formData.location, linkedin: formData.linkedin, github: formData.github };
      if (formData.displayName) payload.displayName = formData.displayName;
      return onSave(payload);
    }
    onSave(formData);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
      <div className="profile-edit-modal" style={{ background: 'var(--surface)', width: '100%', maxWidth: 560, borderRadius: 16, boxShadow: '0 20px 40px rgba(0,0,0,0.3)', position: 'relative', margin: '20px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: 'var(--text)' }}>{TITLES[section] || 'Edit'}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div className="no-scrollbar" style={{ overflowY: 'auto', padding: '0 28px', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 8 }}>

            {/* ── Header / Bio basic info ── */}
            {section === 'header' && (
              <>
                <FieldInput label="Full Name"       value={formData.displayName}    onChange={v => setFormData(p => ({ ...p, displayName: v }))} />
                <FieldInput label="Username"         value={formData.username}       onChange={v => setFormData(p => ({ ...p, username: v }))} placeholder="Only letters & numbers" />
                <FieldInput label="Current Role"     value={formData.currentRole}    onChange={v => setFormData(p => ({ ...p, currentRole: v }))} placeholder="e.g. Software Engineer" />
                <FieldInput label="Current Company"  value={formData.currentCompany} onChange={v => setFormData(p => ({ ...p, currentCompany: v }))} placeholder="e.g. Infosys" />
                <FieldInput label="Location"         value={formData.location}       onChange={v => setFormData(p => ({ ...p, location: v }))} placeholder="e.g. Bangalore" />
              </>
            )}

            {/* ── Headline ── */}
            {section === 'headline' && (
              <FieldInput
                label="Headline"
                value={formData.headline}
                onChange={v => setFormData(p => ({ ...p, headline: v }))}
                placeholder="e.g. Senior SDE · React · Node.js · 5 yrs exp"
                hint="Max 120 characters. This appears right below your name."
              />
            )}

            {/* ── Skills ── */}
            {section === 'skills' && (
              <>
                <FieldInput
                  label="Skills"
                  value={formData.skills.join(', ')}
                  onChange={v => setFormData(p => ({ ...p, skills: v.split(',').map(s => s.trim()).filter(Boolean) }))}
                  placeholder="React, Node.js, Python, SQL, Figma…"
                  hint="Separate each skill with a comma."
                />
                {formData.skills.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {formData.skills.map((s, i) => (
                      <span key={i} style={{ padding: '4px 12px', borderRadius: 20, background: 'var(--surface2)', border: '1px solid var(--border2)', fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>
                        {s}
                        <span
                          onClick={() => setFormData(p => ({ ...p, skills: p.skills.filter((_, idx) => idx !== i) }))}
                          style={{ marginLeft: 6, cursor: 'pointer', opacity: 0.6 }}
                        >×</span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── Employment ── */}
            {section === 'employment' && (
              <>
                {empList.map((job, i) => (
                  <div key={i} style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)' }}>Experience {i + 1}</span>
                      {empList.length > 1 && (
                        <span onClick={() => setEmpList(l => l.filter((_, idx) => idx !== i))} style={{ fontSize: 11, color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>Remove</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <FieldInput label="Job Title / Role" value={job.role}    onChange={v => setEmpList(l => l.map((e, idx) => idx === i ? { ...e, role: v } : e))}    placeholder="Software Engineer" />
                        <FieldInput label="Company"          value={job.company} onChange={v => setEmpList(l => l.map((e, idx) => idx === i ? { ...e, company: v } : e))} placeholder="Infosys" />
                        <FieldInput label="From"             value={job.from}    onChange={v => setEmpList(l => l.map((e, idx) => idx === i ? { ...e, from: v } : e))}    placeholder="Jan 2022" />
                        <FieldInput label="To"               value={job.current ? 'Present' : job.to} onChange={v => setEmpList(l => l.map((e, idx) => idx === i ? { ...e, to: v } : e))} placeholder="Dec 2024" disabled={job.current} />
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!job.current} onChange={e => setEmpList(l => l.map((j, idx) => idx === i ? { ...j, current: e.target.checked } : j))} />
                      Currently working here
                    </label>
                  </div>
                ))}
                <button
                  onClick={() => setEmpList(l => [...l, { ...EMPTY_EMPLOYMENT }])}
                  style={{ padding: '9px 0', borderRadius: 8, border: '1.5px dashed var(--border2)', background: 'var(--surface2)', color: 'var(--blue)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                >
                  + Add another experience
                </button>
              </>
            )}

            {/* ── Education ── */}
            {section === 'education' && (
              <>
                {eduList.map((edu, i) => (
                  <div key={i} style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)' }}>Education {i + 1}</span>
                      {eduList.length > 1 && (
                        <span onClick={() => setEduList(l => l.filter((_, idx) => idx !== i))} style={{ fontSize: 11, color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>Remove</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <FieldInput label="Degree / Course"  value={edu.degree}      onChange={v => setEduList(l => l.map((e, idx) => idx === i ? { ...e, degree: v } : e))}      placeholder="B.Tech CSE" />
                      <FieldInput label="Institution"       value={edu.institution} onChange={v => setEduList(l => l.map((e, idx) => idx === i ? { ...e, institution: v } : e))} placeholder="IIT Delhi" />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <FieldInput label="Year of Passing" value={edu.year}  onChange={v => setEduList(l => l.map((e, idx) => idx === i ? { ...e, year: v } : e))}  placeholder="2022" />
                        <FieldInput label="Grade / CGPA"    value={edu.grade} onChange={v => setEduList(l => l.map((e, idx) => idx === i ? { ...e, grade: v } : e))} placeholder="8.4 / 10" />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setEduList(l => [...l, { ...EMPTY_EDUCATION }])}
                  style={{ padding: '9px 0', borderRadius: 8, border: '1.5px dashed var(--border2)', background: 'var(--surface2)', color: 'var(--blue)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                >
                  + Add another education
                </button>
              </>
            )}

            {/* ── Profile Summary / Bio ── */}
            {section === 'bio' && (
              <FieldTextarea
                label="Profile Summary"
                value={formData.bio}
                onChange={v => setFormData(p => ({ ...p, bio: v }))}
                placeholder="Write about your experience, strengths and what you're looking for…"
                rows={6}
                hint="Max 1000 characters."
              />
            )}

            {/* ── Personal Details ── */}
            {section === 'personal' && (
              <>
                <FieldInput label="Full Name"  value={formData.displayName} onChange={v => setFormData(p => ({ ...p, displayName: v }))} />
                <FieldInput label="Email"      value={formData.email}       onChange={v => setFormData(p => ({ ...p, email: v }))}       type="email" hint="Your email is private." />
                <FieldInput label="Phone"      value={formData.phone}       onChange={v => setFormData(p => ({ ...p, phone: v }))}       placeholder="+91 9876543210" />
                <FieldInput label="Location"   value={formData.location}    onChange={v => setFormData(p => ({ ...p, location: v }))}    placeholder="Bangalore, India" />
                <FieldInput label="LinkedIn URL" value={formData.linkedin}  onChange={v => setFormData(p => ({ ...p, linkedin: v }))}   placeholder="linkedin.com/in/username" />
                <FieldInput label="GitHub URL"   value={formData.github}    onChange={v => setFormData(p => ({ ...p, github: v }))}     placeholder="github.com/username" />
              </>
            )}

          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 28px 24px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          {saveError && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
              {saveError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '10px 24px', borderRadius: 20, border: '1px solid var(--border)', background: 'transparent', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text3)' }}>
              Cancel
            </button>
            <button onClick={handleSaveClick} style={{ padding: '10px 32px', borderRadius: 20, border: 'none', background: 'var(--blue)', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff', boxShadow: '0 4px 12px rgba(68,137,255,0.3)' }}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Field helpers ───────────────────────────────────────────────

function FieldInput({ label, value, onChange, placeholder, hint, type = 'text', disabled = false, style }) {
  return (
    <div style={style}>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 8,
          border: '1px solid var(--border)', fontSize: 13, color: disabled ? 'var(--text3)' : 'var(--text)',
          outline: 'none', background: disabled ? 'var(--surface2)' : 'var(--surface)',
          boxSizing: 'border-box', transition: 'border-color 0.15s',
          fontFamily: 'inherit',
        }}
        onFocus={e => { if (!disabled) e.target.style.borderColor = 'var(--blue)'; }}
        onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
      />
      {hint && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function FieldTextarea({ label, value, onChange, placeholder, hint, rows = 4 }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{label}</label>
      <textarea
        rows={rows}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 8,
          border: '1px solid var(--border)', fontSize: 13, color: 'var(--text)',
          outline: 'none', resize: 'vertical', lineHeight: 1.6,
          boxSizing: 'border-box', fontFamily: 'inherit',
          background: 'var(--surface)',
        }}
        onFocus={e => { e.target.style.borderColor = 'var(--blue)'; }}
        onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
      />
      {hint && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
